/**
 * Race events (§5). CRUD is owner-or-crew — crew log the weekend.
 *
 * A `planned` event carries the estimates the forecast walks. Converting it to
 * `actual` is what advances real usage, so the actuals become required at that
 * moment and not before.
 */

import { z } from 'zod';

import { eventStatus } from './enums.js';
import { count, isoDate, optionalText, shortText, uuid } from './primitives.js';

/**
 * The event's identity is its **name**, not its track.
 *
 * A touring series runs the same event across several tracks over a season, so
 * "track" is not a stable identifier — the name is. Track is therefore optional
 * (a multi-track event simply leaves it blank), and the name is what the
 * timeline headlines.
 */
export const createEventRequest = z.object({
  carId: uuid,
  driverId: uuid.nullable().optional(),
  date: isoDate,
  name: shortText(120),
  track: optionalText(120),
  estPasses: count.max(1000).default(0),
  estMiles: count.max(100_000).default(0),
});
export type CreateEventRequest = z.infer<typeof createEventRequest>;

export const updateEventRequest = z.object({
  driverId: uuid.nullable().optional(),
  date: isoDate.optional(),
  name: shortText(120).optional(),
  track: optionalText(120),
  estPasses: count.max(1000).optional(),
  estMiles: count.max(100_000).optional(),
});
export type UpdateEventRequest = z.infer<typeof updateEventRequest>;

/**
 * `POST /events/:id/actual` — "this is what we actually ran."
 *
 * Actuals are required here: the whole reason to convert an event is to replace
 * an estimate with a fact, and a conversion carrying no numbers would silently
 * zero out the usage the forecast depends on.
 */
export const completeEventRequest = z.object({
  actualPasses: count.max(1000),
  actualMiles: count.max(100_000).default(0),
});
export type CompleteEventRequest = z.infer<typeof completeEventRequest>;

export const eventParam = z.object({ garageId: uuid, eventId: uuid });

export const listEventsQuery = z.object({
  carId: uuid.optional(),
  status: eventStatus.optional(),
  /** Defaults to upcoming-only; the timeline never wants history. */
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type ListEventsQuery = z.infer<typeof listEventsQuery>;
